import { useSyncExternalStore, useCallback } from 'react'
import en from '../locales/en.json'
import ar from '../locales/ar.json'

/**
 * Locale (v0.23.1, D-142) — Arabic + RTL, modelled on `theme.ts` so it reads like the
 * rest of this codebase, with one addition theme does not need: theme is pure CSS and
 * nothing re-renders, but locale lives in React, so it needs a subscription.
 *
 * Two safety properties worth keeping: a missing key renders the KEY (a visible
 * `nav.trees` is a bug report; an empty button is a mystery), and lookup falls back to
 * English per-key, so `ar.json` may lag indefinitely without breaking a screen.
 */

const KEY = 'fa_locale'
export type Locale = 'en' | 'ar'
type Catalogue = Record<string, unknown>
const CATALOGUES: Record<Locale, Catalogue> = { en, ar }

export function preferredLocale(): Locale {
  const saved = localStorage.getItem(KEY)
  if (saved === 'en' || saved === 'ar') return saved
  const langs = navigator.languages?.length ? navigator.languages : [navigator.language || 'en']
  return langs.some(l => l.toLowerCase().startsWith('ar')) ? 'ar' : 'en'
}

export const dirFor = (l: Locale): 'rtl' | 'ltr' => (l === 'ar' ? 'rtl' : 'ltr')

function applyLocale(l: Locale) {
  document.documentElement.lang = l
  document.documentElement.dir = dirFor(l)
}

let current: Locale = 'en'
const listeners = new Set<() => void>()
const subscribe = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn) } }
const snapshot = () => current

export function setLocale(l: Locale) {
  if (l === current) return
  current = l
  localStorage.setItem(KEY, l)
  applyLocale(l)
  listeners.forEach(fn => fn())
}

/** Call before first paint (alongside initTheme) so there is no LTR→RTL flash. */
export function initLocale() {
  current = preferredLocale()
  applyLocale(current)
}

const PLURALS = new Map<Locale, Intl.PluralRules>()
function pluralRules(l: Locale) {
  let r = PLURALS.get(l)
  if (!r) { r = new Intl.PluralRules(l); PLURALS.set(l, r) }
  return r
}

const lookup = (cat: Catalogue, key: string): unknown =>
  key.split('.').reduce<unknown>((o, k) => (o && typeof o === 'object' ? (o as Catalogue)[k] : undefined), cat)

export function translate(locale: Locale, key: string, vars?: Record<string, string | number>): string {
  let node = lookup(CATALOGUES[locale], key)
  if (node === undefined && locale !== 'en') node = lookup(CATALOGUES.en, key)

  if (node && typeof node === 'object' && vars && vars.count !== undefined) {
    const forms = node as Record<string, string>
    const n = Number(vars.count)
    const cat = n === 0 && forms.zero ? 'zero' : pluralRules(locale).select(n)
    node = forms[cat] ?? forms.other
  }
  if (typeof node !== 'string') return key
  return vars ? node.replace(/\{(\w+)\}/g, (m, v) => (v in vars ? String(vars[v]) : m)) : node
}

export function useI18n() {
  const locale = useSyncExternalStore(subscribe, snapshot, snapshot)
  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => translate(locale, key, vars),
    [locale],
  )
  return { t, locale, dir: dirFor(locale), rtl: locale === 'ar', setLocale }
}

/**
 * Rich text in catalogue strings (v0.27.0, D-150). Translations carry inline
 * markers — "Welcome back, [gold]{name}[/gold]" — because word ORDER differs by
 * language: hardcoding <span> boundaries around an interpolated name in JSX bakes
 * English ordering into the markup. The component decides what a tag looks like;
 * the translation decides where it goes.
 */
export interface RichSegment { tag: string | null; text: string }

export function segments(s: string): RichSegment[] {
  const out: RichSegment[] = []
  const re = /\[(\w+)\](.*?)\[\/\1\]/g
  let last = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(s))) {
    if (m.index > last) out.push({ tag: null, text: s.slice(last, m.index) })
    out.push({ tag: m[1], text: m[2] })
    last = re.lastIndex
  }
  if (last < s.length) out.push({ tag: null, text: s.slice(last) })
  return out
}
